/**
 * FonioAnrufButton - Telefon-Button + zentrales Anruf-Modal (Koop-Markendesign).
 * - Rufnummer immer sichtbar (klickbar als Rueckruf), Fallback wenn unterdrueckt
 * - Umfangreiches Termin-Formular (Dauer, Ort, Beschreibung, Zuweisung) -> App-Kalender
 * - Tabs: Aktueller Anruf / Verlauf (Zebra)
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import {
  Phone, PhoneIncoming, PhoneOutgoing, X, Clock, User, Building2,
  ExternalLink, Check, CalendarPlus, MessageSquare, Flame, History, MapPin,
} from 'lucide-react';
import { useEingehendeAnrufe, unlockAudio, requestNotificationPermission } from '@/hooks/useEingehendeAnrufe';
import { useAuth } from '@/contexts/AuthContext';
import {
  callerDisplay, isBekannt, unreadCount, formatGermanPhone, timeAgoDe,
  type EingehenderAnruf,
} from '@/lib/fonioAnruf';

const ROT = '#9B1C1C';
const ROT_HELL = '#C41E3A';

const ALERT_ICON =
  'data:image/svg+xml,' +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><circle cx='16' cy='16' r='15' fill='#C41E3A'/><circle cx='16' cy='16' r='6' fill='white'/></svg>",
  );

function faviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

function defaultTerminStart(): string {
  const d = new Date(Date.now() + 60 * 60000);
  d.setMinutes(0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function FonioAnrufButton() {
  const { calls, popupCall, setStatus, markAlleGesehen, dismissPopup, terminAnlegen } = useEingehendeAnrufe(30);
  const { profile } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [tab, setTab] = useState<'aktuell' | 'verlauf'>('aktuell');
  const [showTermin, setShowTermin] = useState(false);
  const [tTitel, setTTitel] = useState('');
  const [tBeginn, setTBeginn] = useState('');
  const [tDauer, setTDauer] = useState(30);
  const [tOrt, setTOrt] = useState('');
  const [tBeschreibung, setTBeschreibung] = useState('');
  const [saving, setSaving] = useState(false);
  const [, setLocation] = useLocation();

  const offen = unreadCount(calls);
  const aktiv: EingehenderAnruf | null = popupCall ?? calls[0] ?? null;
  const lead = !!aktiv && !isBekannt(aktiv);

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    if (popupCall) {
      setModalOpen(true);
      setTab('aktuell');
      setShowTermin(false);
    }
  }, [popupCall]);

  // Termin-Formular vorbelegen, sobald der aktive Anruf wechselt
  useEffect(() => {
    if (aktiv) {
      setTTitel(`Termin: ${callerDisplay(aktiv)}`);
      setTBeginn(defaultTerminStart());
      setTDauer(30);
      setTOrt('');
      setTBeschreibung(aktiv.anliegen || aktiv.zusammenfassung || '');
    }
  }, [aktiv?.id]);

  // Titel + Favicon blinken solange ein eingehender Anruf aktiv ist
  useEffect(() => {
    if (!popupCall) return;
    const isLead = !isBekannt(popupCall);
    const link = faviconLink();
    const origTitle = document.title;
    const origIcon = link.getAttribute('href');
    let on = false;
    const flash = (isLead ? 'NEUER LEAD - ' : 'NEUER ANRUF - ') + callerDisplay(popupCall);
    const iv = setInterval(() => {
      on = !on;
      document.title = on ? flash : origTitle;
      link.setAttribute('href', on ? ALERT_ICON : origIcon || '');
    }, 800);
    return () => {
      clearInterval(iv);
      document.title = origTitle;
      if (origIcon) link.setAttribute('href', origIcon);
    };
  }, [popupCall]);

  function closeModal() {
    setModalOpen(false);
    setShowTermin(false);
    dismissPopup();
  }

  function openHistory() {
    unlockAudio();
    requestNotificationPermission();
    setTab('verlauf');
    setModalOpen(true);
    if (offen > 0) void markAlleGesehen();
  }

  function openKontakt(c: EingehenderAnruf) {
    if (c.status === 'eingegangen') void setStatus(c.id, 'gesehen');
    if (c.crm_kontakt_id) {
      setLocation(`/kontakte/${c.crm_kontakt_id}`);
      closeModal();
    }
  }

  function gotoKommunikation() {
    setLocation('/kommunikation');
    closeModal();
  }

  async function saveTermin() {
    if (!aktiv || !tBeginn) {
      toast.error('Bitte Datum/Uhrzeit waehlen');
      return;
    }
    setSaving(true);
    const ok = await terminAnlegen(aktiv, {
      titel: tTitel || 'Telefon-Termin',
      beginnLocal: tBeginn,
      dauerMin: tDauer,
      ort: tOrt,
      beschreibung: tBeschreibung,
      mitarbeiterId: profile?.id ?? null,
    });
    setSaving(false);
    if (ok) {
      toast.success('Termin im Kalender angelegt', {
        description: tTitel,
        action: { label: 'Kalender', onClick: () => { setLocation('/kalender'); closeModal(); } },
      });
      setShowTermin(false);
    } else {
      toast.error('Termin konnte nicht angelegt werden');
    }
  }

  const tabCls = (active: boolean) =>
    `flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-semibold border-b-2 transition-colors ${
      active ? 'text-[#9B1C1C] border-[#9B1C1C]' : 'text-gray-500 border-transparent hover:text-gray-700'
    }`;

  const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#C41E3A]';

  return (
    <>
      <button
        onClick={openHistory}
        className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors relative"
        title="Eingehende Anrufe (fonio)"
      >
        <Phone className="w-[18px] h-[18px] text-gray-600" />
        {offen > 0 && (
          <span
            className="absolute top-1 right-1 min-w-4 h-4 px-0.5 text-white text-[9px] font-bold rounded-full flex items-center justify-center"
            style={{ backgroundColor: ROT_HELL }}
          >
            {offen > 9 ? '9+' : offen}
          </span>
        )}
      </button>

      {modalOpen && (
        <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-4 pt-16 sm:pt-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />

          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="h-1 sticky top-0" style={{ backgroundColor: ROT }} />

            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${ROT}14` }}>
                <Phone className="w-[18px] h-[18px]" style={{ color: ROT }} />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-bold text-gray-900 leading-none flex items-center gap-2">
                  {popupCall ? 'Eingehender Anruf' : 'Telefon'}
                  {popupCall && (
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ backgroundColor: ROT_HELL }} />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ROT_HELL }} />
                    </span>
                  )}
                </h2>
                <p className="text-[11px] text-gray-400 mt-1">fonio Telefonie</p>
              </div>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" aria-label="Schliessen">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex border-b border-gray-100">
              <button onClick={() => setTab('aktuell')} className={tabCls(tab === 'aktuell')}>
                <Phone className="w-4 h-4" />Aktueller Anruf
              </button>
              <button onClick={() => setTab('verlauf')} className={tabCls(tab === 'verlauf')}>
                <History className="w-4 h-4" />Verlauf{calls.length > 0 ? ` (${calls.length})` : ''}
              </button>
            </div>

            {tab === 'aktuell' && (
              <div className="px-6 py-5">
                {!aktiv ? (
                  <div className="py-10 text-center text-sm text-gray-400">
                    <Phone className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                    Kein aktueller Anruf
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-4">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-2xl font-semibold" style={{ backgroundColor: `${ROT}12`, color: ROT }}>
                        {isBekannt(aktiv) ? callerDisplay(aktiv).charAt(0).toUpperCase() : <User className="w-8 h-8" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        {lead && (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: ROT_HELL }}>
                            <Flame className="w-3 h-3" />NEUER LEAD
                          </span>
                        )}
                        <div className="truncate text-xl font-bold text-gray-900 mt-0.5">{callerDisplay(aktiv)}</div>
                        {aktiv.anrufer_firma && (
                          <div className="flex items-center gap-1 truncate text-sm text-gray-500">
                            <Building2 className="w-3.5 h-3.5" />{aktiv.anrufer_firma}
                          </div>
                        )}
                        {/* Rufnummer immer anzeigen */}
                        {aktiv.from_number ? (
                          <a href={`tel:${aktiv.from_number}`} className="inline-flex items-center gap-1.5 text-base font-semibold text-gray-800 hover:underline">
                            <Phone className="w-4 h-4 text-gray-400" />{formatGermanPhone(aktiv.from_number)}
                          </a>
                        ) : (
                          <div className="text-sm text-gray-400">Nummer nicht uebermittelt</div>
                        )}
                        {aktiv.to_number && (
                          <div className="text-[11px] text-gray-400">angerufen: {formatGermanPhone(aktiv.to_number)}</div>
                        )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          {aktiv.akten_nummer && (
                            <span className="inline-block rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">Akte {aktiv.akten_nummer}</span>
                          )}
                          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                            <Clock className="w-3 h-3" />{timeAgoDe(aktiv.eingegangen_am)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {(aktiv.anliegen || aktiv.zusammenfassung) && (
                      <div className="mt-4 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 text-sm text-gray-700">
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Anliegen</div>
                        {aktiv.anliegen || aktiv.zusammenfassung}
                      </div>
                    )}

                    {showTermin ? (
                      <div className="mt-4 space-y-3 rounded-xl border border-gray-200 p-4">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1">
                          <CalendarPlus className="w-3.5 h-3.5" />Termin im App-Kalender
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-500 mb-1">Titel</label>
                          <input value={tTitel} onChange={(e) => setTTitel(e.target.value)} placeholder="Titel" className={inputCls} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Beginn</label>
                            <input type="datetime-local" value={tBeginn} onChange={(e) => setTBeginn(e.target.value)} className={inputCls} />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Dauer</label>
                            <select value={tDauer} onChange={(e) => setTDauer(Number(e.target.value))} className={inputCls}>
                              <option value={15}>15 Min</option>
                              <option value={30}>30 Min</option>
                              <option value={45}>45 Min</option>
                              <option value={60}>1 Std</option>
                              <option value={90}>1,5 Std</option>
                              <option value={120}>2 Std</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-500 mb-1">Ort</label>
                          <div className="relative">
                            <MapPin className="w-4 h-4 text-gray-300 absolute left-2.5 top-2.5" />
                            <input value={tOrt} onChange={(e) => setTOrt(e.target.value)} placeholder="z.B. Buero / vor Ort" className={inputCls + ' pl-8'} />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-500 mb-1">Notiz / Beschreibung</label>
                          <textarea value={tBeschreibung} onChange={(e) => setTBeschreibung(e.target.value)} rows={2} className={inputCls + ' resize-none'} />
                        </div>
                        {profile?.name && (
                          <p className="text-[11px] text-gray-400">Zugewiesen an: {profile.name}</p>
                        )}
                        <div className="flex gap-2 pt-1">
                          <button onClick={saveTermin} disabled={saving} className="flex-1 rounded-lg px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ROT_HELL }}>
                            {saving ? 'Speichern...' : 'Termin speichern'}
                          </button>
                          <button onClick={() => setShowTermin(false)} className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-5 space-y-2">
                        {aktiv.crm_kontakt_id && (
                          <button onClick={() => openKontakt(aktiv)} className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-white hover:opacity-95" style={{ backgroundColor: ROT_HELL }}>
                            <ExternalLink className="w-4 h-4" />Kontakt oeffnen
                          </button>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                          {aktiv.from_number && (
                            <a href={`tel:${aktiv.from_number}`} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                              <PhoneOutgoing className="w-4 h-4" />Rueckruf
                            </a>
                          )}
                          <button onClick={() => setShowTermin(true)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            <CalendarPlus className="w-4 h-4" />Termin
                          </button>
                          <button onClick={() => { void setStatus(aktiv.id, 'erledigt'); closeModal(); }} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            <Check className="w-4 h-4" />Erledigt
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {tab === 'verlauf' && (
              <div className="max-h-[26rem] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                {calls.length === 0 && (
                  <div className="px-4 py-12 text-center">
                    <Phone className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-400">Keine Anrufe</p>
                  </div>
                )}
                {calls.map((c, idx) => {
                  const bekannt = isBekannt(c);
                  return (
                    <div
                      key={c.id}
                      className={`flex items-start gap-3 px-6 py-3 border-b border-gray-100 transition-colors ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'} ${c.crm_kontakt_id ? 'hover:bg-gray-100 cursor-pointer' : 'hover:bg-gray-100/60'}`}
                      onClick={() => c.crm_kontakt_id && openKontakt(c)}
                    >
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={bekannt ? { backgroundColor: `${ROT}12`, color: ROT } : { backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>
                        <PhoneIncoming className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate flex-1">{callerDisplay(c)}</p>
                          <span className="text-[10px] text-gray-400 flex-shrink-0 flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" />{timeAgoDe(c.eingegangen_am)}
                          </span>
                        </div>
                        {!bekannt && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold" style={{ color: ROT_HELL }}>
                            <Flame className="w-2.5 h-2.5" />NEUER LEAD
                          </span>
                        )}
                        <p className="text-[11px] text-gray-500">{aktivNummer(c)}</p>
                        {(c.anliegen || c.zusammenfassung) && (
                          <p className="mt-1 text-[11px] text-gray-600 line-clamp-2 flex items-start gap-1">
                            <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
                            <span>{c.anliegen || c.zusammenfassung}</span>
                          </p>
                        )}
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {c.akten_nummer && (
                            <span className="inline-block rounded-md bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-600">Akte {c.akten_nummer}</span>
                          )}
                          {c.crm_kontakt_id && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium" style={{ color: ROT }}>
                              <ExternalLink className="w-2.5 h-2.5" />Kontakt
                            </span>
                          )}
                        </div>
                      </div>
                      {c.status !== 'erledigt' && (
                        <button onClick={(e) => { e.stopPropagation(); void setStatus(c.id, 'erledigt'); }} className="p-1 rounded hover:bg-gray-200 text-gray-300 hover:text-gray-600" title="Als erledigt markieren">
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
              <button onClick={gotoKommunikation} className="text-xs font-semibold hover:underline" style={{ color: ROT }}>
                Zum Kommunikationsbereich
              </button>
              <button onClick={closeModal} className="text-xs font-medium text-gray-400 hover:underline">
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function aktivNummer(c: EingehenderAnruf): string {
  return c.from_number ? formatGermanPhone(c.from_number) : 'Nummer nicht uebermittelt';
}
